import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, vModelDynamic as _vModelDynamic, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "card-global space-y-6" }
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = { class: "page-title" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = {
  key: 1,
  class: "input-form-error-text"
}
const _hoisted_6 = { class: "rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm" }
const _hoisted_7 = { class: "space-y-4" }
const _hoisted_8 = { class: "block-title" }
const _hoisted_9 = { class: "col-span-12 md:col-span-5" }
const _hoisted_10 = { class: "font-medium" }
const _hoisted_11 = { class: "text-sm text-gray-500" }
const _hoisted_12 = { class: "col-span-12 md:col-span-7" }
const _hoisted_13 = { class: "input-form-label" }
const _hoisted_14 = ["onUpdate:modelValue", "onChange"]
const _hoisted_15 = { value: null }
const _hoisted_16 = ["value"]
const _hoisted_17 = {
  key: 0,
  class: "space-y-5"
}
const _hoisted_18 = { class: "block-title" }
const _hoisted_19 = { class: "mb-3 font-medium" }
const _hoisted_20 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_21 = { class: "input-form-label" }
const _hoisted_22 = ["onUpdate:modelValue"]
const _hoisted_23 = ["onUpdate:modelValue"]
const _hoisted_24 = /*#__PURE__*/_createElementVNode("option", { value: "" }, null, -1)
const _hoisted_25 = ["value"]
const _hoisted_26 = ["onUpdate:modelValue", "type"]
const _hoisted_27 = { class: "mt-3" }
const _hoisted_28 = { class: "cursor-pointer text-primary-300" }
const _hoisted_29 = { class: "mt-3 grid gap-3" }
const _hoisted_30 = { class: "col-span-12 md:col-span-4" }
const _hoisted_31 = ["onUpdate:modelValue", "type", "placeholder"]
const _hoisted_32 = {
  key: 1,
  class: "space-y-4"
}
const _hoisted_33 = { class: "block-title" }
const _hoisted_34 = { class: "mb-3 font-medium" }
const _hoisted_35 = { class: "flex flex-wrap gap-2" }
const _hoisted_36 = { class: "flex justify-end" }
const _hoisted_37 = { key: 3 }
const _hoisted_38 = { class: "mt-4 flex justify-end" }

import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Editor from "@tinymce/tinymce-vue";
import Modal from "@/components/common/Modal.vue";
import { t } from "@/i18n";
import DocumentDataService from "@/services/documents/document/DocumentDataService";
import DocumentTemplateDataService from "@/services/documents/documentTemplate/DocumentTemplateDataService";
import { fullTinyMceConfig, tinyMceScriptSrc } from "@/utils/tinymceConfig";
import {
  BatchFieldValues,
  BatchLogicalField,
  BatchTemplate,
  buildTemplateFieldConfig,
  createLogicalFields,
  formatTemplateFieldValue,
  isEmptyBatchValue,
  logicalKeyForTag,
  normalizeTemplateTag,
} from "@/utils/documentBatchFields";

type DraftFile = {
  fileGuid: string;
  type: "MAIN" | "SUB";
  name: string;
  editableSource?: any;
};
type Draft = { id: string; title: string; data: any; files: DraftFile[] };
type Slot = {
  key: string;
  type: "MAIN" | "SUB";
  index: number;
  label: string;
  fileNames: string[];
  templateId: string | null;
  template?: BatchTemplate;
};


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const route = useRoute();
const router = useRouter();
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const drafts = ref<Draft[]>([]);
const slots = ref<Slot[]>([]);
const templates = ref<Array<{ id: string; name: string }>>([]);
const commonValues = reactive<BatchFieldValues>({});
const individualValues = reactive<Record<string, BatchFieldValues>>({});
const editedHtml = reactive<Record<string, string>>({});
const previewOpen = ref(false);
const previewHtml = ref("");
const previewTitle = ref("");
const previewKey = ref("");

const mappedSlots = computed(() => slots.value.filter((slot) => slot.template));
const fields = computed<BatchLogicalField[]>(() =>
  createLogicalFields(mappedSlots.value.map((slot) => slot.template!))
);
const scopes = computed(() =>
  fields.value.reduce((result, field) => {
    result[field.key] = "INDIVIDUAL";
    return result;
  }, {} as Record<string, "INDIVIDUAL">)
);

const valuesFor = (draftId: string): BatchFieldValues => {
  const overrides = individualValues[draftId] || {};
  return fields.value.reduce((result, field) => {
    result[field.key] = isEmptyBatchValue(overrides[field.key])
      ? commonValues[field.key] || ""
      : overrides[field.key];
    return result;
  }, {} as BatchFieldValues);
};

const dateLogicalKeys = new Set([
  "work_start_date",
  "agreement_date",
  "acknowledgement_date",
  "leave_start_date",
  "leave_end_date",
  "dismissal_date",
  "contract_date",
]);

const cloneFieldValue = (value: any) =>
  Array.isArray(value) ? [...value] : value == null ? "" : String(value);

const sameFieldValue = (left: any, right: any) =>
  JSON.stringify(cloneFieldValue(left)) ===
  JSON.stringify(cloneFieldValue(right));

function savedFieldValue(source: any, tag: string) {
  const values = source?.fieldValues || {};
  const normalized = normalizeTemplateTag(tag);
  const candidates = [
    tag,
    normalized,
    `key_${normalized}`,
    `{{${normalized}}}`,
  ];
  const key = candidates.find((candidate) =>
    Object.prototype.hasOwnProperty.call(values, candidate)
  );
  return key == null ? undefined : values[key];
}

function monthNumber(value: unknown) {
  const text = String(value || "")
    .trim()
    .toLocaleLowerCase();
  const numeric = Number(text);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) {
    return String(numeric).padStart(2, "0");
  }
  for (let month = 0; month < 12; month += 1) {
    for (const locale of ["ru-RU", "en-US"]) {
      const part = new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        timeZone: "UTC",
      })
        .formatToParts(new Date(Date.UTC(2020, month, 1)))
        .find((item) => item.type === "month")?.value;
      if (part?.toLocaleLowerCase() === text) {
        return String(month + 1).padStart(2, "0");
      }
    }
  }
  return "";
}

function valuesFromSource(source: any): BatchFieldValues {
  const result: BatchFieldValues = {};
  const dateParts: Record<
    string,
    { day?: string; month?: string; year?: string }
  > = {};
  const sourceValues = source?.fieldValues || {};
  (source?.manualFields || []).forEach((field: any) => {
    const normalized = normalizeTemplateTag(field.tag);
    const logicalKey = logicalKeyForTag(field.tag);
    // Compatibility with drafts saved by the previous batch editor, which
    // persisted logical aliases instead of the template's original tags.
    if (Object.prototype.hasOwnProperty.call(sourceValues, logicalKey)) {
      result[logicalKey] = cloneFieldValue(sourceValues[logicalKey]);
      return;
    }
    const value = savedFieldValue(source, field.tag);
    if (value === undefined) return;
    const datePart = normalized.match(/_(day|month|year)$/)?.[1];
    if (dateLogicalKeys.has(logicalKey) && datePart) {
      dateParts[logicalKey] ||= {};
      dateParts[logicalKey][datePart as "day" | "month" | "year"] =
        String(value);
      return;
    }
    result[logicalKey] = cloneFieldValue(value);
  });
  Object.entries(dateParts).forEach(([key, parts]) => {
    const month = monthNumber(parts.month);
    if (parts.year && month && parts.day) {
      result[key] = `${parts.year}-${month}-${String(parts.day).padStart(
        2,
        "0"
      )}`;
    }
  });
  return result;
}

function savedValuesForDraft(draft: Draft): BatchFieldValues {
  const result: BatchFieldValues = {};
  const conflicts = new Set<string>();
  draft.files.forEach((file) => {
    Object.entries(valuesFromSource(file.editableSource)).forEach(
      ([key, value]) => {
        if (conflicts.has(key) || isEmptyBatchValue(value)) return;
        if (!(key in result)) {
          result[key] = cloneFieldValue(value);
        } else if (!sameFieldValue(result[key], value)) {
          delete result[key];
          conflicts.add(key);
        }
      }
    );
  });
  return result;
}

function hydrateSavedValues() {
  const savedByDraft = new Map(
    drafts.value.map((draft) => [draft.id, savedValuesForDraft(draft)])
  );
  fields.value.forEach((field) => {
    const entries = drafts.value.map(
      (draft) => savedByDraft.get(draft.id)?.[field.key]
    );
    const populated = entries.filter((value) => !isEmptyBatchValue(value));
    const shared =
      populated.length === drafts.value.length &&
      populated.every((value) => sameFieldValue(value, populated[0]));
    commonValues[field.key] = shared
      ? cloneFieldValue(populated[0])
      : field.type === "MULTI_SELECT"
      ? []
      : "";
    drafts.value.forEach((draft, index) => {
      individualValues[draft.id] ||= {};
      individualValues[draft.id][field.key] = shared
        ? field.type === "MULTI_SELECT"
          ? []
          : ""
        : cloneFieldValue(entries[index]);
    });
  });
}

const fileForSlot = (draft: Draft, slot: Slot) =>
  draft.files.filter((file) => file.type === slot.type)[slot.index];

async function loadSlotTemplate(slot: Slot) {
  slot.template = undefined;
  if (!slot.templateId) return;
  const response = await DocumentTemplateDataService.get(slot.templateId);
  slot.template = response.data.data as BatchTemplate;
  createLogicalFields([slot.template]).forEach((field) => {
    if (commonValues[field.key] == null) commonValues[field.key] = "";
    drafts.value.forEach((draft) => {
      if (!individualValues[draft.id]) individualValues[draft.id] = {};
      if (individualValues[draft.id][field.key] == null)
        individualValues[draft.id][field.key] = "";
    });
  });
}

function renderTemplate(template: BatchTemplate, draftId: string) {
  let html = template.template || "";
  buildTemplateFieldConfig(
    template,
    {},
    valuesFor(draftId),
    scopes.value
  ).forEach((field) => {
    const value = formatTemplateFieldValue(
      field.value || "",
      field.type,
      field.outputFormat
    );
    html = html.split(field.key).join(value);
    if (!field.key.startsWith("key_"))
      html = html.split(`key_${field.key}`).join(value);
  });
  return html;
}

function editKey(draft: Draft, slot: Slot) {
  return `${draft.id}:${slot.key}`;
}
function openPreview(draft: Draft, slot: Slot) {
  if (!slot.template) return;
  previewKey.value = editKey(draft, slot);
  previewTitle.value = `${draft.title} — ${slot.label}`;
  previewHtml.value =
    editedHtml[previewKey.value] || renderTemplate(slot.template, draft.id);
  previewOpen.value = true;
}
function savePreview() {
  editedHtml[previewKey.value] = previewHtml.value;
  closePreview();
}
function closePreview() {
  previewOpen.value = false;
  previewHtml.value = "";
  previewKey.value = "";
}

function validate() {
  for (const draft of drafts.value) {
    const values = valuesFor(draft.id);
    for (const field of fields.value) {
      if (field.required && isEmptyBatchValue(values[field.key])) {
        return `${draft.title}: ${field.label}`;
      }
    }
  }
  return "";
}

async function updateDraft(draft: Draft) {
  const nextFiles = [...draft.files];
  for (const slot of mappedSlots.value) {
    const oldFile = fileForSlot(draft, slot);
    if (!oldFile || !slot.template) continue;
    const fieldConfig = buildTemplateFieldConfig(
      slot.template,
      {},
      valuesFor(draft.id),
      scopes.value
    );
    const sourceFieldValues = fieldConfig.reduce((result, field) => {
      result[field.key] = Array.isArray(field.values)
        ? [...field.values]
        : field.value;
      return result;
    }, {} as BatchFieldValues);
    const customHtml = editedHtml[editKey(draft, slot)];
    const editorHtml = customHtml || renderTemplate(slot.template, draft.id);
    const response = await DocumentTemplateDataService.convert({
      name: oldFile.name.replace(/\.[^.]+$/, ""),
      templateId: slot.template.id,
      template: `<div style="font-family: 'times new roman', times, serif;">${
        customHtml || slot.template.template
      }</div>`,
      fieldConfig: customHtml ? [] : fieldConfig,
    });
    const index = nextFiles.findIndex(
      (file) => file.fileGuid === oldFile.fileGuid
    );
    nextFiles.splice(index, 1, {
      fileGuid: response.data.data.fileGuid,
      type: oldFile.type,
      name: oldFile.name,
      editableSource: {
        templateId: slot.template.id,
        templateName: slot.template.name,
        fileName: oldFile.name.replace(/\.[^.]+$/, ""),
        templateHtml: slot.template.template,
        editorHtml,
        manualFields: slot.template.manualFields || [],
        fieldValues: sourceFieldValues,
      },
    });
  }
  const info = draft.data.documentInfo;
  await DocumentDataService.update(draft.id, {
    id: draft.id,
    constructorId: draft.data.constructorInfo.id,
    linkedDocId: null,
    documentInfo: { ...info, nomenclature: info.nomenclatureInfo.id },
    documentFiles: nextFiles.map(({ name, ...file }) => file),
    documentProcessesInfo: draft.data.documentProcessesInfo,
  });
}

async function applyChanges() {
  error.value = validate();
  if (error.value) return;
  saving.value = true;
  try {
    let cursor = 0;
    const workers = Array.from(
      { length: Math.min(2, drafts.value.length) },
      async () => {
        while (cursor < drafts.value.length)
          await updateDraft(drafts.value[cursor++]);
      }
    );
    await Promise.all(workers);
    router.push({ name: "DocumentList", query: { type: "ALL" } });
  } catch (reason: any) {
    error.value =
      reason?.response?.data?.message || reason?.message || String(reason);
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  const ids = String(route.query.ids || "")
    .split(",")
    .filter(Boolean);
  if (!ids.length) {
    loading.value = false;
    return;
  }
  try {
    const templateResponse = await DocumentTemplateDataService.getAll({
      page: 1,
    });
    templates.value = templateResponse.data.data.items || [];
    for (const id of ids) {
      const response = await DocumentDataService.getOneDocumentUpdateInfo(id);
      const data = response.data.data;
      const fileResponse = await DocumentDataService.getFilesInfo(
        data.documentFiles.map((file: any) => file.fileGuid)
      );
      const infoByGuid = new Map(
        (fileResponse.data || []).map((item: any) => [item.guid, item])
      );
      const files = data.documentFiles
        .filter((file: any) => file.type === "MAIN" || file.type === "SUB")
        .map((file: any) => {
          const info: any = infoByGuid.get(file.fileGuid);
          return {
            ...file,
            name: info
              ? `${info.name}${info.ext}`
              : file.editableSource?.fileName || file.fileGuid,
          };
        });
      drafts.value.push({ id, title: data.documentInfo.title, data, files });
      individualValues[id] = {};
    }
    const maxMain = Math.max(
      ...drafts.value.map(
        (draft) => draft.files.filter((file) => file.type === "MAIN").length
      )
    );
    const maxSub = Math.max(
      ...drafts.value.map(
        (draft) => draft.files.filter((file) => file.type === "SUB").length
      )
    );
    [
      ...[...Array(maxMain)].map((_, index) => ({
        type: "MAIN" as const,
        index,
      })),
      ...[...Array(maxSub)].map((_, index) => ({
        type: "SUB" as const,
        index,
      })),
    ].forEach(({ type, index }) => {
      const files = drafts.value
        .map((draft) => draft.files.filter((file) => file.type === type)[index])
        .filter(Boolean);
      const sourceTemplateIds = Array.from(
        new Set(
          files.map((file) => file.editableSource?.templateId).filter(Boolean)
        )
      );
      slots.value.push({
        key: `${type}:${index}`,
        type,
        index,
        label:
          type === "MAIN"
            ? t("docForm.mainFile")
            : `${t("docForm.extraFiles")} ${index + 1}`,
        fileNames: Array.from(new Set(files.map((file) => file.name))),
        templateId:
          sourceTemplateIds.length === 1 ? sourceTemplateIds[0] : null,
      });
    });
    for (const slot of slots.value.filter((item) => item.templateId))
      await loadSlotTemplate(slot);
    hydrateSavedValues();
  } catch (reason: any) {
    error.value =
      reason?.response?.data?.message || reason?.message || String(reason);
  } finally {
    loading.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)("documentDraftBatch.title")), 1),
        _createVNode(_component_Button, {
          variant: "default",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).back()))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("common.back")), 1)
          ]),
          _: 1
        })
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(t)("common.loading")), 1))
        : _createCommentVNode("", true),
      (error.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(error.value), 1))
        : _createCommentVNode("", true),
      (!loading.value && drafts.value.length)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)("documentDraftBatch.legacyWarning")), 1),
            _createElementVNode("section", _hoisted_7, [
              _createElementVNode("h2", _hoisted_8, _toDisplayString(_unref(t)("documentDraftBatch.mapping")), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slots.value, (slot) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: slot.key,
                  class: "grid grid-cols-12 items-end gap-4 rounded-lg border border-gray-200 p-4"
                }, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, _toDisplayString(slot.label), 1),
                    _createElementVNode("div", _hoisted_11, _toDisplayString(slot.fileNames.join(" · ")), 1)
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("label", _hoisted_13, _toDisplayString(_unref(t)("docForm.mainFileSelectTemplate")), 1),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": ($event: any) => ((slot.templateId) = $event),
                      class: "input-form w-full",
                      onChange: ($event: any) => (loadSlotTemplate(slot))
                    }, [
                      _createElementVNode("option", _hoisted_15, _toDisplayString(_unref(t)("common.notSelected")), 1),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(templates.value, (item) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: item.id,
                          value: item.id
                        }, _toDisplayString(item.name), 9, _hoisted_16))
                      }), 128))
                    ], 40, _hoisted_14), [
                      [_vModelSelect, slot.templateId]
                    ])
                  ])
                ]))
              }), 128))
            ]),
            (_unref(fields).length)
              ? (_openBlock(), _createElementBlock("section", _hoisted_17, [
                  _createElementVNode("h2", _hoisted_18, _toDisplayString(_unref(t)("documentDraftBatch.values")), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(fields), (field) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: field.key,
                      class: "rounded-lg border border-gray-200 p-4"
                    }, [
                      _createElementVNode("div", _hoisted_19, [
                        _createTextVNode(_toDisplayString(field.label) + " ", 1),
                        (field.required)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_20, "*"))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("label", _hoisted_21, _toDisplayString(_unref(t)("documentDraftBatch.commonValue")), 1),
                      (field.type === 'TEXTAREA')
                        ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                            key: 0,
                            "onUpdate:modelValue": ($event: any) => ((_unref(commonValues)[field.key]) = $event),
                            class: "input-form w-full",
                            rows: "2"
                          }, null, 8, _hoisted_22)), [
                            [_vModelText, _unref(commonValues)[field.key]]
                          ])
                        : (field.type === 'SELECT')
                          ? _withDirectives((_openBlock(), _createElementBlock("select", {
                              key: 1,
                              "onUpdate:modelValue": ($event: any) => ((_unref(commonValues)[field.key]) = $event),
                              class: "input-form w-full"
                            }, [
                              _hoisted_24,
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.options, (option) => {
                                return (_openBlock(), _createElementBlock("option", {
                                  key: option.value,
                                  value: option.value
                                }, _toDisplayString(option.label), 9, _hoisted_25))
                              }), 128))
                            ], 8, _hoisted_23)), [
                              [_vModelSelect, _unref(commonValues)[field.key]]
                            ])
                          : _withDirectives((_openBlock(), _createElementBlock("input", {
                              key: 2,
                              "onUpdate:modelValue": ($event: any) => ((_unref(commonValues)[field.key]) = $event),
                              type: 
              field.type === 'DATE'
                ? 'date'
                : field.type === 'DATETIME'
                ? 'datetime-local'
                : 'text'
            ,
                              class: "input-form w-full"
                            }, null, 8, _hoisted_26)), [
                              [_vModelDynamic, _unref(commonValues)[field.key]]
                            ]),
                      _createElementVNode("details", _hoisted_27, [
                        _createElementVNode("summary", _hoisted_28, _toDisplayString(_unref(t)("documentDraftBatch.individualValues")), 1),
                        _createElementVNode("div", _hoisted_29, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(drafts.value, (draft) => {
                            return (_openBlock(), _createElementBlock("label", {
                              key: draft.id,
                              class: "grid grid-cols-12 items-center gap-3"
                            }, [
                              _createElementVNode("span", _hoisted_30, _toDisplayString(draft.title), 1),
                              _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": ($event: any) => ((_unref(individualValues)[draft.id][field.key]) = $event),
                                type: 
                    field.type === 'DATE'
                      ? 'date'
                      : field.type === 'DATETIME'
                      ? 'datetime-local'
                      : 'text'
                  ,
                                placeholder: String(_unref(commonValues)[field.key] || ''),
                                class: "input-form col-span-12 md:col-span-8 w-full"
                              }, null, 8, _hoisted_31), [
                                [_vModelDynamic, _unref(individualValues)[draft.id][field.key]]
                              ])
                            ]))
                          }), 128))
                        ])
                      ])
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (_unref(mappedSlots).length)
              ? (_openBlock(), _createElementBlock("section", _hoisted_32, [
                  _createElementVNode("h2", _hoisted_33, _toDisplayString(_unref(t)("documentDraftBatch.preview")), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(drafts.value, (draft) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: draft.id,
                      class: "rounded-lg border border-gray-200 p-4"
                    }, [
                      _createElementVNode("div", _hoisted_34, _toDisplayString(draft.title), 1),
                      _createElementVNode("div", _hoisted_35, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(mappedSlots), (slot) => {
                          return (_openBlock(), _createBlock(_component_Button, {
                            key: slot.key,
                            variant: "default",
                            onClick: ($event: any) => (openPreview(draft, slot))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(slot.label), 1)
                            ]),
                            _: 2
                          }, 1032, ["onClick"]))
                        }), 128))
                      ])
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_36, [
              _createVNode(_component_Button, {
                disabled: saving.value || !_unref(mappedSlots).length,
                onClick: applyChanges
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)("documentDraftBatch.apply")), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ])
          ], 64))
        : (!loading.value && !error.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_37, _toDisplayString(_unref(t)("documentDraftBatch.noEditable")), 1))
          : _createCommentVNode("", true)
    ]),
    _createVNode(Modal, {
      show: previewOpen.value,
      title: previewTitle.value,
      wide: true,
      onClose: closePreview
    }, {
      default: _withCtx(() => [
        (previewOpen.value)
          ? (_openBlock(), _createBlock(_unref(Editor), {
              key: 0,
              modelValue: previewHtml.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((previewHtml).value = $event)),
              init: _unref(fullTinyMceConfig),
              "tinymce-script-src": _unref(tinyMceScriptSrc),
              inline: false
            }, null, 8, ["modelValue", "init", "tinymce-script-src"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_38, [
          _createVNode(_component_Button, { onClick: savePreview }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("common.save")), 1)
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["show", "title"])
  ], 64))
}
}

})